import { Link } from 'react-router-dom'
import useSettings from '../../hooks/Settings'
import { client } from '../../hooks/Client'
import { buildShareQuestionnairePath } from '../../helpers/questionnaires/shareQuestionnairePath'
import usePersonaTerms from '../../hooks/usePersonaTerms'
import isFlagOn from '../../helpers/isFlagOn'
import ToggleButton from '../common/ToggleButton'
import QualifyingQuestionsRow from './QualifyingQuestionsRow'
import { qualifyingSetsFor } from '../../helpers/questionnaire/sectorQualifyingQuestions'

// AI-CONTEXT: See docs/features/automatische-deelname-onderzoek.md

/**
 * Voor wie geldt dit onderzoek: alle huishoudens, alle medewerkers, of alleen
 * wie met de hand gekoppeld is.
 * Plus, met huishoudens: of de vragenlijst eerst om het adres vraagt, en met
 * deelnemers: of hij eerst om de naam vraagt — allebei zodat één algemene
 * QR-code volstaat. Die twee sluiten elkaar uit: staat de ene aan, dan toont de
 * andere uitleg in plaats van een schakelaar (de server weigert de combinatie ook).
 *
 * Eén schakelaar per module die aanstaat. Tot 2026-09-15 moest elk huishouden
 * en elke medewerker apart aan een onderzoek gekoppeld worden, en dat werd bij
 * 38% van de mensen die op naam meedoen nooit gedaan — die bestonden voor de
 * signalering niet. De keuze staat op het onderzoek, want daar richt je in wat
 * je meet en bij wie.
 *
 * De schakelaar schrijft met dot-notation in de map `AppliesTo`, zodat de twee
 * vlaggen elkaar niet overschrijven. Lezers gaan door `isResearchForAll`.
 *
 * Niets tonen zolang de instellingen laden: een schakelaar die 'uit' leest
 * omdat de module nog niet bekend is, is een onware bewering.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {object} props
 * @param {{docid: string, ID?: string, QuestionnaireID?: string, AppliesTo?: {allHouseholds?: *, allPersonas?: *}, AskAddressFirst?: *, AskNameFirst?: *}} props.research -
 *   Het onderzoek zoals de bouwer het uit Firestore krijgt (mét docid)
 */
const ResearchAppliesToSettings = ({ research }) => {
    const { loaded, housevisits, personasOn, selectedSectors } = useSettings()
    const personaTerms = usePersonaTerms()

    if (!loaded) return null

    const asksAddress = isFlagOn(research?.AskAddressFirst)
    const asksName = isFlagOn(research?.AskNameFirst)

    const rows = [
        housevisits && {
            id: 'AppliesTo.allHouseholds',
            title: 'Geldt voor alle huishoudens',
            text: 'Elk huishouden doet mee; je hoeft niets per huishouden te koppelen.',
            value: research?.AppliesTo?.allHouseholds,
        },
        // Adresstap: één algemene QR-code in plaats van één per huishouden. Alleen
        // met huishoudens, want daar koppelt het adres aan. Plat veld (niet in
        // AppliesTo): het zegt niet vóór wie het onderzoek geldt, maar hoe de
        // invulling bij iemand terechtkomt. matchQuestionnaireAddress leest hem
        // met isFlagOn.
        // AI-CONTEXT: See docs/features/adres-koppeling-algemene-qr.md
        housevisits && {
            id: 'AskAddressFirst',
            title: 'Vraag eerst om het adres',
            text: 'Werk met één algemene QR-code: wie de vragenlijst invult, vult eerst postcode en huisnummer in, en de antwoorden komen bij het juiste huishouden.',
            value: research?.AskAddressFirst,
            blockedBy: asksName ? "Kan niet samen met 'Vraag eerst om de naam'." : '',
            sharesOneLink: asksAddress,
        },
        personasOn === 'flex' && {
            id: 'AppliesTo.allPersonas',
            title: `Geldt voor alle ${personaTerms.pluralLower}`,
            text: `Elke actieve ${personaTerms.singularLower} doet mee; je hoeft niets per ${personaTerms.singularLower} te koppelen.`,
            value: research?.AppliesTo?.allPersonas,
        },
        // Naamstap: één algemene link; wie invult geeft zijn naam, en een
        // medewerker koppelt de invulling op de kaart 'Deelnemers om te koppelen'.
        // Nooit automatisch: een naam is niet uniek. matchQuestionnaireName leest
        // hem met isFlagOn.
        // AI-CONTEXT: See docs/features/deelnemer-koppeling-algemene-link.md
        personasOn === 'flex' && {
            id: 'AskNameFirst',
            title: 'Vraag eerst om de naam',
            text: `Werk met één algemene QR-code: wie de vragenlijst invult, vult eerst voor- en achternaam in, en jij koppelt de antwoorden daarna aan de juiste ${personaTerms.singularLower}.`,
            value: research?.AskNameFirst,
            blockedBy: asksAddress ? "Kan niet samen met 'Vraag eerst om het adres'." : '',
            sharesOneLink: asksName,
        },
    ].filter(Boolean)

    if (rows.length === 0) return null

    return (
        <div className='research-settings-group'>
            <h4 className='research-settings-group-title'>Wie doet mee</h4>
            {rows.map((row) => (
                <div key={row.id} className='reasearch-builder-option-container'>
                    <div className='research-builder-options-title-container'>
                        <p><b>{row.title}</b></p>
                        <p>{row.text}</p>
                        {/* De ene link staat op 'Vragenlijst delen'; wie de stap net
                            aanzette, zocht hem hier (testronde 2026-10-13, B3). */}
                        {row.sharesOneLink && research?.QuestionnaireID && (
                            <p>
                                <Link className='research-settings-share-link' to={buildShareQuestionnairePath({ client, questionnaireID: research.QuestionnaireID, researchID: research.ID })}>
                                    Link of QR-code maken
                                </Link>
                            </p>
                        )}
                    </div>
                    {row.blockedBy ? (
                        <p>{row.blockedBy}</p>
                    ) : (
                        <ToggleButton
                            docid={research?.docid}
                            id={row.id}
                            value={row.value}
                            collection='Research'
                        />
                    )}
                </div>
            ))}
            {/* Kwalificeringsvragen per sector: alleen met huishoudens, want ze
                gaan over het huishouden. Standaard uit; de begeleider zet hem aan
                bij de onboarding. AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md */}
            {housevisits && qualifyingSetsFor(selectedSectors).map((set) => (
                <QualifyingQuestionsRow key={set.key} research={research} set={set} />
            ))}
        </div>
    )
}

export default ResearchAppliesToSettings
